(() => {
  const roots = document.querySelectorAll('[data-unisource-all-reviews]');
  if (!roots.length) return;

  const MAX_ATTEMPTS = 40;
  const RETRY_DELAY = 300;

  function cleanText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function toArray(value) {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }

  function clampRating(value) {
    const number = Number.parseFloat(value);
    if (!Number.isFinite(number)) return 5;
    return Math.max(0, Math.min(5, number));
  }

  function starString(rating) {
    const rounded = Math.round(clampRating(rating));
    return '★★★★★'.slice(0, rounded) + '☆☆☆☆☆'.slice(0, 5 - rounded);
  }

  function parseDate(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  function formatDate(value) {
    const date = parseDate(value);
    if (!date) return cleanText(value);

    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function flattenJsonLd(node, output = []) {
    if (!node) return output;

    if (Array.isArray(node)) {
      node.forEach((item) => flattenJsonLd(item, output));
      return output;
    }

    if (typeof node !== 'object') return output;

    output.push(node);

    if (node['@graph']) flattenJsonLd(node['@graph'], output);
    if (node.review) flattenJsonLd(node.review, output);
    if (node.itemReviewed) flattenJsonLd(node.itemReviewed, output);
    if (node.aggregateRating) flattenJsonLd(node.aggregateRating, output);

    return output;
  }

  function parseJsonLdFromRoot(root) {
    const scripts = Array.from(
      root.querySelectorAll(
        'script[type="application/ld+json"], script.json-ld-content'
      )
    );

    const parsed = [];

    scripts.forEach((script) => {
      const raw = cleanText(script.textContent);
      if (!raw || raw === '{}') return;

      try {
        parsed.push(JSON.parse(raw));
      } catch (_error) {
        // GatherUp can write incomplete JSON while the widget initializes.
      }
    });

    return parsed;
  }

  function reviewKey(review) {
    return [
      cleanText(review.author).toLowerCase(),
      cleanText(review.dateRaw || review.date).toLowerCase(),
      cleanText(review.body).toLowerCase().slice(0, 120),
    ].join('|');
  }

  function dedupeReviews(reviews) {
    const seen = new Set();

    return reviews.filter((review) => {
      const key = reviewKey(review);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function extractFromJsonLd(jsonLdItems) {
    const nodes = [];
    jsonLdItems.forEach((item) => flattenJsonLd(item, nodes));

    const reviews = [];
    let aggregate = null;

    nodes.forEach((node) => {
      const type = toArray(node['@type']).map((entry) =>
        String(entry).toLowerCase()
      );

      if (
        !aggregate &&
        node.ratingValue &&
        (node.reviewCount ||
          node.ratingCount ||
          type.includes('aggregaterating'))
      ) {
        aggregate = {
          rating: clampRating(node.ratingValue),
          count: cleanText(node.reviewCount || node.ratingCount || ''),
        };
      }

      if (type.includes('review') || node.reviewBody || node.reviewRating) {
        const rating =
          node.reviewRating?.ratingValue || node.ratingValue || 5;
        const author =
          typeof node.author === 'object' ? node.author.name : node.author;
        const dateRaw = node.datePublished || node.dateCreated || '';
        const title = node.name || node.headline || 'Customer review';
        const body = node.reviewBody || node.description || '';

        if (cleanText(body) || cleanText(title)) {
          reviews.push({
            rating: clampRating(rating),
            title: cleanText(title),
            body: cleanText(body),
            author: cleanText(author || 'Verified customer'),
            date: formatDate(dateRaw),
            dateRaw: cleanText(dateRaw),
            timestamp: parseDate(dateRaw)?.getTime() || 0,
          });
        }
      }
    });

    return {
      aggregate,
      reviews: dedupeReviews(reviews),
    };
  }

  function extractFromVisibleWidget(root) {
    const source = root.querySelector('[data-gatherup-source]');
    if (!source) return { aggregate: null, reviews: [] };

    const reviewNodes = Array.from(
      source.querySelectorAll(
        '[itemprop="review"], [class*="review"], [class*="testimonial"]'
      )
    ).filter((node) => cleanText(node.textContent).length > 40);

    const reviews = reviewNodes.map((node) => {
      const ratingNode = node.querySelector(
        '[itemprop="ratingValue"], [class*="star"], [class*="rating"]'
      );
      const authorNode = node.querySelector(
        '[itemprop="author"], [class*="author"], [class*="name"]'
      );
      const bodyNode = node.querySelector(
        '[itemprop="reviewBody"], [class*="body"], [class*="text"], p'
      );
      const titleNode = node.querySelector(
        '[itemprop="name"], h3, h4, strong'
      );
      const dateNode = node.querySelector(
        '[itemprop="datePublished"], time, [class*="date"]'
      );

      const dateRaw =
        dateNode?.getAttribute('datetime') || dateNode?.textContent || '';
      const body = cleanText(bodyNode?.textContent || node.textContent);

      return {
        rating: clampRating(
          ratingNode?.getAttribute('content') ||
            ratingNode?.textContent ||
            5
        ),
        title: cleanText(titleNode?.textContent || 'Customer review'),
        body,
        author: cleanText(
          authorNode?.textContent || 'Verified customer'
        ),
        date: formatDate(dateRaw),
        dateRaw: cleanText(dateRaw),
        timestamp: parseDate(dateRaw)?.getTime() || 0,
      };
    });

    return {
      aggregate: null,
      reviews: dedupeReviews(reviews),
    };
  }

  function mergeData(jsonData, domData) {
    return {
      aggregate: jsonData.aggregate || domData.aggregate,
      reviews: dedupeReviews([
        ...jsonData.reviews,
        ...domData.reviews,
      ]),
    };
  }

  function cardHtml(review) {
    const title = cleanText(review.title || 'Customer review');
    const body = cleanText(
      review.body ||
        'Great service and support from the Unisource Apparel team.'
    );
    const author = cleanText(review.author || 'Verified customer');
    const date = cleanText(review.date || '');
    const rating = clampRating(review.rating || 5);

    return `
      <article class="unisource-all-reviews__card">
        <div
          class="unisource-all-reviews__stars"
          aria-label="${rating} out of 5 stars"
        >${starString(rating)}</div>
        <strong>${escapeHtml(title)}</strong>
        <p>${escapeHtml(body)}</p>
        <div class="unisource-all-reviews__meta">
          ${escapeHtml(author)}
          ${
            date
              ? `<span class="unisource-all-reviews__date">${escapeHtml(
                  date
                )}</span>`
              : ''
          }
        </div>
      </article>
    `;
  }

  function initRoot(root) {
    if (root.dataset.reviewsInitialized === 'true') return;
    root.dataset.reviewsInitialized = 'true';

    const grid = root.querySelector('[data-all-reviews-grid]');
    const empty = root.querySelector('[data-all-reviews-empty]');
    const summary = root.querySelector('[data-all-reviews-summary]');
    const toolbar = root.querySelector('[data-all-reviews-toolbar]');
    const ratingEl = root.querySelector('[data-all-reviews-rating]');
    const starsEl = root.querySelector('[data-all-reviews-stars]');
    const countEl = root.querySelector('[data-all-reviews-count]');
    const resultCountEl = root.querySelector(
      '[data-all-reviews-result-count]'
    );
    const ratingFilter = root.querySelector(
      '[data-all-reviews-rating-filter]'
    );
    const sortSelect = root.querySelector('[data-all-reviews-sort]');
    const loadMoreButton = root.querySelector(
      '[data-all-reviews-load-more]'
    );

    const initialCount =
      Number.parseInt(root.dataset.initialCount || '12', 10) || 12;
    const loadMoreCount =
      Number.parseInt(root.dataset.loadMoreCount || '8', 10) || 8;

    let attempts = 0;
    let allReviews = [];
    let visibleCount = initialCount;
    let aggregate = null;

    function filteredAndSortedReviews() {
      const selectedRating = ratingFilter?.value || 'all';
      const selectedSort = sortSelect?.value || 'newest';

      let reviews = [...allReviews];

      if (selectedRating !== 'all') {
        const target = Number.parseInt(selectedRating, 10);
        reviews = reviews.filter(
          (review) => Math.round(review.rating) === target
        );
      }

      reviews.sort((a, b) => {
        if (selectedSort === 'oldest') {
          return (a.timestamp || 0) - (b.timestamp || 0);
        }

        if (selectedSort === 'highest') {
          return b.rating - a.rating || (b.timestamp || 0) - (a.timestamp || 0);
        }

        if (selectedSort === 'lowest') {
          return a.rating - b.rating || (b.timestamp || 0) - (a.timestamp || 0);
        }

        return (b.timestamp || 0) - (a.timestamp || 0);
      });

      return reviews;
    }

    function render() {
      if (!grid) return false;

      const reviews = filteredAndSortedReviews();
      const visibleReviews = reviews.slice(0, visibleCount);

      if (!reviews.length) {
        grid.innerHTML = '';
        if (empty) empty.hidden = false;
        if (loadMoreButton) loadMoreButton.hidden = true;
        if (resultCountEl) resultCountEl.textContent = '0 reviews';
        return false;
      }

      if (empty) empty.hidden = true;
      grid.innerHTML = visibleReviews.map(cardHtml).join('');

      if (resultCountEl) {
        resultCountEl.textContent =
          visibleReviews.length === reviews.length
            ? `${reviews.length} review${reviews.length === 1 ? '' : 's'}`
            : `Showing ${visibleReviews.length} of ${reviews.length} reviews`;
      }

      if (loadMoreButton) {
        loadMoreButton.hidden = visibleReviews.length >= reviews.length;
      }

      return true;
    }

    function applySummary() {
      if (!summary) return;

      summary.hidden = false;

      const rating = aggregate?.rating || 5;
      if (ratingEl) ratingEl.textContent = rating.toFixed(1);
      if (starsEl) {
        starsEl.textContent = starString(rating);
        starsEl.setAttribute(
          'aria-label',
          `${rating} out of 5 stars`
        );
      }

      if (countEl) {
        if (aggregate?.count) {
          countEl.textContent = `Based on ${aggregate.count} customer reviews`;
        } else {
          countEl.textContent = `${allReviews.length} customer review${
            allReviews.length === 1 ? '' : 's'
          } currently available`;
        }
      }

      if (toolbar) toolbar.hidden = false;
    }

    function tryRender() {
      attempts += 1;

      const jsonData = extractFromJsonLd(parseJsonLdFromRoot(root));
      const domData = extractFromVisibleWidget(root);
      const data = mergeData(jsonData, domData);

      if (data.reviews.length) {
        aggregate = data.aggregate;
        allReviews = data.reviews;
        applySummary();
        render();
        return;
      }

      if (attempts < MAX_ATTEMPTS) {
        window.setTimeout(tryRender, RETRY_DELAY);
      } else {
        if (grid) grid.innerHTML = '';
        if (empty) {
          empty.hidden = false;
          const message = empty.querySelector('p');
          if (message) {
            message.textContent =
              'Customer reviews could not be loaded. Please refresh the page or try again later.';
          }
        }
      }
    }

    ratingFilter?.addEventListener('change', () => {
      visibleCount = initialCount;
      render();
    });

    sortSelect?.addEventListener('change', () => {
      visibleCount = initialCount;
      render();
    });

    loadMoreButton?.addEventListener('click', () => {
      visibleCount += loadMoreCount;
      render();
    });

    tryRender();

    const source = root.querySelector('[data-gatherup-source]');
    if (source) {
      const observer = new MutationObserver(() => {
        const jsonData = extractFromJsonLd(parseJsonLdFromRoot(root));
        const domData = extractFromVisibleWidget(root);
        const data = mergeData(jsonData, domData);

        if (data.reviews.length > allReviews.length) {
          aggregate = data.aggregate || aggregate;
          allReviews = data.reviews;
          applySummary();
          render();
        }
      });

      observer.observe(source, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  roots.forEach(initRoot);
})();
